import { useState, useEffect, useMemo } from "react";
import { ChevronRight, BarChart3, BookOpen, Layers, Grid3X3, RefreshCw } from "lucide-react";
import api from "../services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────

function pct(val, total) {
  if (!total) return 0;
  return Math.round((val / total) * 100);
}

// ── Doluluk çubuğu (Capacity bar) ─────────────────────────────────────────────
// Gösterir: bu öğenin parent toplamına oransal doluluk + total sayı

function CapacityBar({ value, parentTotal, size = "md" }) {
  const p = parentTotal > 0 ? Math.round((value / parentTotal) * 100) : 0;
  const barH = size === "sm" ? "h-4" : "h-6";
  const numClass = size === "sm"
    ? "text-xs font-bold text-slate-700 w-8 text-right shrink-0"
    : "text-sm font-bold text-slate-800 w-12 text-right shrink-0";

  return (
    <div className="flex items-center gap-2 shrink-0" style={{ width: size === "sm" ? 148 : 200 }}>
      {/* Bar */}
      <div className={`flex-1 relative ${barH} bg-slate-100 rounded-md overflow-hidden`}>
        <div
          className="absolute inset-y-0 left-0 rounded-md transition-all duration-700 ease-out"
          style={{
            width: `${p > 0 ? Math.max(p, 2) : 0}%`,
            background: size === "sm"
              ? "linear-gradient(90deg, #60a5fa, #818cf8)"
              : "linear-gradient(90deg, #3b82f6, #6366f1)"
          }}
        />
        {/* Percentage text */}
        {p > 18 ? (
          <span
            className="absolute inset-0 flex items-center px-2"
            style={{ fontSize: size === "sm" ? 9 : 11, fontWeight: 700, color: "#fff" }}
          >
            %{p}
          </span>
        ) : (
          <span
            className="absolute inset-0 flex items-center justify-end pr-2"
            style={{ fontSize: size === "sm" ? 9 : 10, fontWeight: 600, color: "#94a3b8" }}
          >
            %{p}
          </span>
        )}
      </div>
      {/* Total number */}
      <span className={numClass}>{value.toLocaleString("tr-TR")}</span>
    </div>
  );
}

// ── Tür dağılımı segmentli bar (ders seviyesi) ────────────────────────────────

function TypeSegmentBar({ byType, total }) {
  const tPct = pct(byType.text, total);
  const iPct = pct(byType.image, total);
  const vPct = Math.max(0, 100 - tPct - iPct);
  return (
    <div className="flex h-2.5 rounded-full overflow-hidden w-36 shrink-0" title={`Metin %${tPct} · Görsel %${iPct} · Video %${vPct}`}>
      <div style={{ width: `${tPct}%`, background: "#3b82f6" }} />
      <div style={{ width: `${iPct}%`, background: "#8b5cf6" }} />
      <div style={{ width: `${vPct}%`, background: "#f97316" }} />
    </div>
  );
}

// ── Stat chip (ders kartı başlığı) ────────────────────────────────────────────

function Chip({ label, value, total, color, bg }) {
  return (
    <div className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg ${bg} min-w-[52px]`}>
      <span className={`text-xs font-semibold ${color}`}>{value.toLocaleString("tr-TR")}</span>
      <span className="text-[10px] text-slate-500 mt-0.5">{label}</span>
      {total > 0 && (
        <span className={`text-[9px] font-medium ${color} opacity-70`}>%{pct(value, total)}</span>
      )}
    </div>
  );
}

// ── Alt Konu satırı ───────────────────────────────────────────────────────────

function SubtopicRow({ data, topicTotal }) {
  const { subtopic, total, by_type, by_difficulty } = data;
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-slate-50 transition-colors"
      data-testid="subtopic-row"
    >
      <div className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0 ml-1" />
      <span className="text-xs text-slate-600 truncate min-w-0 flex-1" title={subtopic}>{subtopic}</span>

      {/* Küçük doluluk çubuğu */}
      <CapacityBar value={total} parentTotal={topicTotal} size="sm" />

      <div className="w-px h-3 bg-slate-200 shrink-0" />
      <span className="text-[11px] text-blue-600 w-6 text-right shrink-0">{by_type.text}</span>
      <span className="text-[11px] text-violet-600 w-6 text-right shrink-0">{by_type.image}</span>
      <span className="text-[11px] text-orange-500 w-6 text-right shrink-0">{by_type.video}</span>
      <div className="w-px h-3 bg-slate-200 shrink-0" />
      <span className="text-[11px] text-emerald-600 w-6 text-right shrink-0">{by_difficulty.easy}</span>
      <span className="text-[11px] text-amber-500 w-6 text-right shrink-0">{by_difficulty.medium}</span>
      <span className="text-[11px] text-red-500 w-6 text-right shrink-0">{by_difficulty.hard}</span>
    </div>
  );
}

// ── Konu satırı ───────────────────────────────────────────────────────────────

function TopicRow({ data, lessonTotal }) {
  const [open, setOpen] = useState(false);
  const { topic, total, by_type, by_difficulty, subtopics } = data;
  const hasChildren = subtopics && subtopics.length > 0;

  return (
    <div>
      <button
        onClick={() => hasChildren && setOpen(v => !v)}
        className={`w-full flex items-center gap-2 py-2 px-2 rounded-lg transition-colors text-left
          ${hasChildren ? "hover:bg-white/70 cursor-pointer" : "cursor-default"}
          ${open ? "bg-white shadow-sm" : ""}`}
        data-testid="topic-row"
      >
        {/* Chevron */}
        <div className="w-5 shrink-0 text-slate-400">
          {hasChildren && (
            <ChevronRight
              size={13}
              className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`}
            />
          )}
        </div>

        {/* Topic name */}
        <span
          className="text-sm text-slate-700 font-medium truncate min-w-0 flex-1"
          title={topic}
        >
          {topic}
        </span>

        {/* ★ Doluluk çubuğu + toplam sayı */}
        <CapacityBar value={total} parentTotal={lessonTotal} size="md" />

        <div className="w-px h-3 bg-slate-200 shrink-0" />
        <span className="text-xs font-medium text-blue-600 w-8 text-right shrink-0">{by_type.text}</span>
        <span className="text-xs font-medium text-violet-600 w-8 text-right shrink-0">{by_type.image}</span>
        <span className="text-xs font-medium text-orange-500 w-8 text-right shrink-0">{by_type.video}</span>
        <div className="w-px h-3 bg-slate-200 shrink-0" />
        <span className="text-xs font-medium text-emerald-600 w-8 text-right shrink-0">{by_difficulty.easy}</span>
        <span className="text-xs font-medium text-amber-500 w-8 text-right shrink-0">{by_difficulty.medium}</span>
        <span className="text-xs font-medium text-red-500 w-8 text-right shrink-0">{by_difficulty.hard}</span>
      </button>

      {/* Alt konular */}
      {open && hasChildren && (
        <div className="ml-5 border-l-2 border-slate-100 pl-2 mt-0.5 space-y-0.5 pb-1">
          {subtopics.map((s, i) => (
            <SubtopicRow key={i} data={s} topicTotal={total} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ders kartı ────────────────────────────────────────────────────────────────

function LessonCard({ data, grandTotal, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const { lesson_name, total, by_type, by_difficulty, topics } = data;
  const lessonPct = pct(total, grandTotal);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="lesson-card">
      {/* ── Ders başlığı ── */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-white hover:bg-slate-50 transition-colors text-left"
        data-testid="lesson-toggle"
      >
        {/* Chevron */}
        <ChevronRight
          size={16}
          className={`text-slate-400 transition-transform duration-200 shrink-0 ${open ? "rotate-90" : ""}`}
        />

        {/* Icon */}
        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
          <BookOpen size={15} className="text-white" />
        </div>

        {/* Ders adı */}
        <div className="flex flex-col min-w-0 flex-1">
          <span className="font-semibold text-slate-800 text-sm truncate">{lesson_name}</span>
          {/* Tür dağılımı segmentli bar + açıklama */}
          <div className="flex items-center gap-2 mt-1">
            <TypeSegmentBar byType={by_type} total={total} />
            <span className="text-[10px] text-slate-400 hidden sm:block">
              Met.{pct(by_type.text, total)}% · Gör.{pct(by_type.image, total)}% · Vid.{pct(by_type.video, total)}%
            </span>
          </div>
        </div>

        {/* Chips */}
        <div className="hidden sm:flex items-center gap-1.5 shrink-0">
          {/* Toplam özel chip — büyük ve belirgin */}
          <div className="flex flex-col items-center px-3 py-1.5 rounded-lg bg-slate-800 min-w-[60px]">
            <span className="text-sm font-bold text-white">{total.toLocaleString("tr-TR")}</span>
            <span className="text-[10px] text-slate-300">Toplam</span>
            {grandTotal > 0 && (
              <span className="text-[9px] text-blue-300 font-medium">%{lessonPct}</span>
            )}
          </div>
          <div className="w-px h-8 bg-slate-200 mx-0.5" />
          <Chip label="Metin" value={by_type.text} total={total} color="text-blue-600" bg="bg-blue-50" />
          <Chip label="Görsel" value={by_type.image} total={total} color="text-violet-600" bg="bg-violet-50" />
          <Chip label="Video" value={by_type.video} total={total} color="text-orange-500" bg="bg-orange-50" />
          <div className="w-px h-8 bg-slate-200 mx-0.5" />
          <Chip label="Kolay" value={by_difficulty.easy} total={total} color="text-emerald-600" bg="bg-emerald-50" />
          <Chip label="Orta" value={by_difficulty.medium} total={total} color="text-amber-500" bg="bg-amber-50" />
          <Chip label="Zor" value={by_difficulty.hard} total={total} color="text-red-500" bg="bg-red-50" />
        </div>

        {/* Mobile: sadece toplam */}
        <div className="sm:hidden flex flex-col items-end shrink-0">
          <span className="text-base font-bold text-slate-800">{total.toLocaleString("tr-TR")}</span>
          <span className="text-[10px] text-slate-400">%{lessonPct}</span>
        </div>
      </button>

      {/* ── Konu listesi ── */}
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 pt-3 pb-3">
          {/* Sütun başlıkları */}
          <div className="flex items-center gap-2 pb-2 mb-1 border-b border-slate-100">
            <div className="w-5 shrink-0" />
            <span className="flex-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Konu</span>
            <div className="shrink-0 text-[10px] font-semibold text-slate-400 uppercase tracking-wider"
              style={{ width: 200, textAlign: "center" }}>
              Doluluk / Top.
            </div>
            <div className="w-px h-3 bg-transparent shrink-0" />
            <span className="text-[10px] font-semibold text-blue-400 w-8 text-right shrink-0">Met.</span>
            <span className="text-[10px] font-semibold text-violet-400 w-8 text-right shrink-0">Gör.</span>
            <span className="text-[10px] font-semibold text-orange-400 w-8 text-right shrink-0">Vid.</span>
            <div className="w-px h-3 bg-transparent shrink-0" />
            <span className="text-[10px] font-semibold text-emerald-400 w-8 text-right shrink-0">Kol.</span>
            <span className="text-[10px] font-semibold text-amber-400 w-8 text-right shrink-0">Ort.</span>
            <span className="text-[10px] font-semibold text-red-400 w-8 text-right shrink-0">Zor</span>
          </div>

          <div className="space-y-0.5">
            {topics.map((t, i) => (
              <TopicRow key={i} data={t} lessonTotal={total} />
            ))}
          </div>

          {topics.length === 0 && (
            <p className="text-xs text-slate-400 py-3 text-center">Konu bulunamadı</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Özet kart ─────────────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
        <Icon size={19} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-none">
          {Number(value).toLocaleString("tr-TR")}
        </p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

// ── Ana sayfa ─────────────────────────────────────────────────────────────────

export default function TaxonomyStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/statistics/taxonomy");
      setData(res.data);
    } catch {
      setError("İstatistikler yüklenemedi. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const filteredLessons = useMemo(() => {
    if (!data?.lessons) return [];
    if (!search.trim()) return data.lessons;
    const q = search.toLowerCase();
    return data.lessons
      .map(l => {
        if (l.lesson_name.toLowerCase().includes(q)) return l;
        const filteredTopics = l.topics.filter(
          t => t.topic.toLowerCase().includes(q) ||
            t.subtopics.some(s => s.subtopic.toLowerCase().includes(q))
        );
        if (!filteredTopics.length) return null;
        return { ...l, topics: filteredTopics };
      })
      .filter(Boolean);
  }, [data, search]);

  const grandTotal = data?.summary?.total_questions || 0;

  return (
    <div className="p-6 space-y-5 max-w-7xl mx-auto" data-testid="taxonomy-stats-page">

      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 size={20} className="text-blue-600" />
            Soru Dağılımı
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Ders → Konu → Alt Konu bazında soru envanteri
          </p>
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          data-testid="refresh-stats-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Yenile
        </button>
      </div>

      {/* Özet kartlar */}
      {data?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard icon={BarChart3} label="Toplam Soru" value={data.summary.total_questions} accent="bg-blue-600" />
          <SummaryCard icon={BookOpen} label="Ders" value={data.summary.lesson_count} accent="bg-violet-600" />
          <SummaryCard icon={Layers} label="Konu" value={data.summary.topic_count} accent="bg-emerald-600" />
          <SummaryCard icon={Grid3X3} label="Alt Konu" value={data.summary.subtopic_count} accent="bg-orange-500" />
        </div>
      )}

      {/* Renk kodları */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
        <span className="font-semibold">Tür:</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" /> Metin</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" /> Görsel</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> Video</span>
        <span className="text-slate-300 mx-1">|</span>
        <span className="font-semibold">Zorluk:</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Kolay</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /> Orta</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" /> Zor</span>
        <span className="text-slate-300 mx-1">|</span>
        <span className="font-semibold">Çubuk:</span>
        <span>Konunun ders toplamına oransal doluluk yüzdesi</span>
      </div>

      {/* Arama */}
      <input
        type="text"
        placeholder="Ders, konu veya alt konu ara..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        data-testid="stats-search-input"
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white"
      />

      {/* İçerik */}
      {loading && (
        <div className="flex items-center justify-center py-24" data-testid="stats-loading">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600" data-testid="stats-error">
          {error}
        </div>
      )}

      {!loading && !error && filteredLessons.length === 0 && (
        <div className="text-center py-16 text-slate-400 text-sm" data-testid="stats-empty">
          {search ? "Aramanızla eşleşen sonuç bulunamadı." : "Henüz soru verisi yok."}
        </div>
      )}

      {!loading && !error && filteredLessons.length > 0 && (
        <div className="space-y-3" data-testid="lessons-list">
          {filteredLessons.map((lesson, i) => (
            <LessonCard
              key={lesson.lesson_name}
              data={lesson}
              grandTotal={grandTotal}
              defaultOpen={filteredLessons.length === 1 || i === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}
